import styles from '../styles/Home.module.scss';
import {
  Box,
  Button,
  HStack,
  Stack,
  Heading,
  Text,
  List,
  ListItem,
  ListIcon,
  VStack,
  SimpleGrid,
  Icon,
  Link as ChakraLink,
} from '@chakra-ui/react';
import { CheckCircleIcon, EmailIcon, ExternalLinkIcon } from '@chakra-ui/icons';
import Link from 'next/link';
import { useIntl } from 'react-intl';
import { AppHead } from '@/components/AppHead';
import { Breadcrumb } from '@/components/Breadcrumb';
import { skuMetaList } from '@lib/config';

const discordLink = 'https://discord.gg/uYDZpkd87V';
const mirrorLink =
  'https://mirror.xyz/0xcBf641a213E742f60DEBa3bF5ae4ee87cA295Bd5';
const twitterLink = 'https://twitter.com/DiscordSuperMan';
// 结构化数据的价格区间，取自购买页实际在售的 SKU
const skuPrices = skuMetaList.map((sku) => sku.ethPrice);

export default function Home() {
  const intl = useIntl();

  const pageTitle = intl.formatMessage({
    defaultMessage: 'Discord自动聊天机器人 - AI智能助手 | Discord SuperMan',
    id: 'home.page.title',
  });

  const pageDescription = intl.formatMessage({
    defaultMessage:
      'AI 驱动的 Discord 自动聊天机器人：支持 AI 自动聊天和自动回复，多账号多频道同时发言，定时批量发送公告和活动消息。无需提供账号密码，数据保存在本地。适用于社区活跃、群组运营、Web3 社区等级提升等场景，支持 Windows 和 macOS。',
    id: 'home.page.description',
  });

  const pageKeywords = intl.formatMessage({
    defaultMessage:
      'Discord机器人,Discord自动聊天,AI聊天机器人,Discord助手,群组管理,社区互动,智能对话,Discord工具,聊天自动化,Discord SuperMan,社区管理工具,AI客服',
    id: 'home.page.keywords',
  });

  // 首页专用的结构化数据 - 整合为更连贯的图谱
  const homeStructuredData = [
    // 主要的软件应用信息
    {
      '@context': 'https://schema.org',
      '@type': 'SoftwareApplication',
      '@id': 'https://discordsuperman.xyz#software',
      name: intl.formatMessage({
        defaultMessage: 'Discord SuperMan',
        id: 'app.name',
      }),
      applicationCategory: 'BusinessApplication',
      operatingSystem: ['Windows', 'macOS'],
      description: pageDescription,
      url: 'https://discordsuperman.xyz',
      image: 'https://discordsuperman.xyz/assets/discord-superman-logo.png',
      author: {
        '@type': 'Organization',
        '@id': 'https://discordsuperman.xyz#organization',
        name: 'Discord SuperMan',
        url: 'https://discordsuperman.xyz',
        email: 'hi@discordsuperman.xyz',
        logo: 'https://discordsuperman.xyz/assets/discord-superman-logo.png',
        sameAs: [
          'https://twitter.com/DiscordSuperMan',
          'https://discord.gg/uYDZpkd87V',
        ],
      },
      offers: {
        '@type': 'AggregateOffer',
        lowPrice: String(Math.min(...skuPrices)),
        highPrice: String(Math.max(...skuPrices)),
        offerCount: skuPrices.length,
        priceCurrency: 'ETH',
        availability: 'https://schema.org/InStock',
        url: 'https://discordsuperman.xyz/buy',
      },
      featureList: [
        intl.formatMessage({
          defaultMessage: '内置最先进的 AI 模型，支持 AI 自动聊天互动',
          id: 'home.features.ai_model',
        }),
        intl.formatMessage({
          defaultMessage: '支持同时使用多个 Discord 账号和频道发言',
          id: 'home.features.multi_account',
        }),
        intl.formatMessage({
          defaultMessage: '支持定时发送预设消息文案，例如公告、活动等',
          id: 'home.features.scheduled_messages',
        }),
        intl.formatMessage({
          defaultMessage: '自动排程多账号多频道，批量定时发送消息',
          id: 'home.features.auto_scheduling',
        }),
        intl.formatMessage({
          defaultMessage: '无需账密，用户数据保存在本地，安全有保障',
          id: 'home.features.security',
        }),
        intl.formatMessage({
          defaultMessage: '丰富的自定义设置，可实现各种多样化需求场景',
          id: 'home.features.customization',
        }),
        intl.formatMessage({
          defaultMessage: '订阅期免费获得功能更新，持续获得最佳用户体验',
          id: 'home.features.updates',
        }),
        intl.formatMessage({
          defaultMessage: '全网独家支持在 macOS 和 windows 双系统使用',
          id: 'home.features.cross_platform',
        }),
      ],
    },
    // 网站信息
    {
      '@context': 'https://schema.org',
      '@type': 'WebSite',
      name: intl.formatMessage({
        defaultMessage: 'Discord SuperMan',
        id: 'app.name',
      }),
      url: 'https://discordsuperman.xyz',
      description: pageDescription,
      publisher: {
        '@id': 'https://discordsuperman.xyz#organization',
      },
      potentialAction: {
        '@type': 'BuyAction',
        target: {
          '@type': 'EntryPoint',
          urlTemplate: 'https://discordsuperman.xyz/buy',
          actionPlatform: [
            'http://schema.org/DesktopWebPlatform',
            'http://schema.org/MobileWebPlatform',
          ],
        },
        object: {
          '@id': 'https://discordsuperman.xyz#software',
        },
      },
    },
  ];

  return (
    <>
      <AppHead
        title={pageTitle}
        description={pageDescription}
        keywords={pageKeywords}
        type="website"
        structuredData={homeStructuredData}
      />

      <Breadcrumb />

      <Stack gap={4}>
        {/* 产品介绍文案 */}
        <Text as="h1" fontSize="lg" color="gray.700" mb={4}>
          {intl.formatMessage({
            defaultMessage:
              '基于先进AI技术的Discord自动聊天机器人，让您的社区更加活跃和智能',
            id: 'home.product.intro',
          })}
        </Text>

        {/* 功能特性列表 */}
        <List spacing={3} className={styles.featList}>
          <ListItem>
            <ListIcon as={CheckCircleIcon} color="green.500" />
            <Text as="span" fontWeight="500">
              {intl.formatMessage({
                defaultMessage: '内置最先进的 AI 模型，支持 AI 自动聊天互动',
                id: 'home.features.ai_model',
              })}
            </Text>
          </ListItem>
          <ListItem>
            <ListIcon as={CheckCircleIcon} color="green.500" />
            <Text as="span" fontWeight="500">
              {intl.formatMessage({
                defaultMessage: '支持同时使用多个 Discord 账号和频道发言',
                id: 'home.features.multi_account',
              })}
            </Text>
          </ListItem>
          <ListItem>
            <ListIcon as={CheckCircleIcon} color="green.500" />
            <Text as="span" fontWeight="500">
              {intl.formatMessage({
                defaultMessage: '支持定时发送预设消息文案，例如公告、活动等',
                id: 'home.features.scheduled_messages',
              })}
            </Text>
          </ListItem>
          <ListItem>
            <ListIcon as={CheckCircleIcon} color="green.500" />
            <Text as="span" fontWeight="500">
              {intl.formatMessage({
                defaultMessage: '自动排程多账号多频道，批量定时发送消息',
                id: 'home.features.auto_scheduling',
              })}
            </Text>
          </ListItem>
          <ListItem>
            <ListIcon as={CheckCircleIcon} color="green.500" />
            <Text as="span">
              {intl.formatMessage({
                defaultMessage: '无需账密，用户数据保存在本地，安全有保障',
                id: 'home.features.security',
              })}
            </Text>
          </ListItem>
          <ListItem>
            <ListIcon as={CheckCircleIcon} color="green.500" />
            <Text as="span">
              {intl.formatMessage({
                defaultMessage: '丰富的自定义设置，可实现各种多样化需求场景',
                id: 'home.features.customization',
              })}
            </Text>
          </ListItem>
          <ListItem>
            <ListIcon as={CheckCircleIcon} color="green.500" />
            <Text as="span">
              {intl.formatMessage({
                defaultMessage: '订阅期免费获得功能更新，持续获得最佳用户体验',
                id: 'home.features.updates',
              })}
            </Text>
          </ListItem>
          <ListItem>
            <ListIcon as={CheckCircleIcon} color="green.500" />
            <Text as="span" fontWeight="500">
              {intl.formatMessage({
                defaultMessage: '全网独家支持在 macOS 和 windows 双系统使用',
                id: 'home.features.cross_platform',
              })}
            </Text>
          </ListItem>
        </List>

        {/* CTA按钮区域 */}
        <HStack gap={2}>
          <Link href="/buy">
            <a>
              <Button colorScheme="blue">
                {intl.formatMessage({
                  defaultMessage: '立即购买',
                  id: 'home.cta.buy_now',
                })}
              </Button>
            </a>
          </Link>
          <Link href="/buy">
            <a>
              <Button>
                {intl.formatMessage({
                  defaultMessage: '登录账户',
                  id: 'home.cta.login',
                })}
              </Button>
            </a>
          </Link>
        </HStack>

        {/* 产品截图展示 */}
        <Box className={styles.screenshot}>
          <ScreenShot
            src="/assets/screenshot/home.png"
            alt={intl.formatMessage({
              defaultMessage: 'Discord聊天助手首页-全局配置',
              id: 'home.screenshots.global_config',
            })}
          />
          <ScreenShot
            src="/assets/screenshot/bot-config-ai.jpg"
            alt={intl.formatMessage({
              defaultMessage: 'Discord聊天助手首页-机器人 AI 模式配置',
              id: 'home.screenshots.ai_config',
            })}
          />
          <ScreenShot
            src="/assets/screenshot/bot-config.png"
            alt={intl.formatMessage({
              defaultMessage: 'Discord聊天助手首页-机器人配置',
              id: 'home.screenshots.bot_config',
            })}
          />
          <ScreenShot
            src="/assets/screenshot/bot-reply-ai.jpg"
            alt={intl.formatMessage({
              defaultMessage: 'Discord聊天助手首页-AI 自动聊天日志',
              id: 'home.screenshots.ai_chat_log',
            })}
          />
          <ScreenShot
            src="/assets/screenshot/log.png"
            alt={intl.formatMessage({
              defaultMessage: 'Discord聊天助手首页-机器人自动回复运行日志',
              id: 'home.screenshots.reply_log',
            })}
          />
          <ScreenShot
            src="/assets/screenshot/batch-reply.png"
            alt={intl.formatMessage({
              defaultMessage: 'Discord聊天助手首页-批量自动回复',
              id: 'home.screenshots.batch_reply',
            })}
          />
        </Box>

        {/* 联系方式 */}
        <Box as="section" mt={8}>
          <Heading as="h2" size="lg" mb={6} textAlign="left" color="gray.700">
            {intl.formatMessage({
              defaultMessage: '联系我们',
              id: 'home.contact.section_title',
            })}
          </Heading>

          <SimpleGrid columns={{ base: 1, md: 2 }} spacing={4} mb={6}>
            <Box
              p={5}
              borderRadius="md"
              bg="gray.50"
              border="1px"
              borderColor="gray.200"
              minH="100px"
              display="flex"
              alignItems="center"
            >
              <HStack spacing={4} w="full" align="center">
                <Icon
                  as={EmailIcon}
                  color="blue.500"
                  boxSize={6}
                  flexShrink={0}
                />
                <VStack align="flex-start" spacing={2} flex={1}>
                  <Text fontWeight="semibold" color="gray.800" fontSize="md">
                    {intl.formatMessage({
                      defaultMessage: '邮箱',
                      id: 'home.contact.email_label',
                    })}
                  </Text>
                  <ChakraLink
                    href="mailto:hi@discordsuperman.xyz"
                    color="blue.500"
                    fontWeight="medium"
                    fontSize="sm"
                    _hover={{ textDecoration: 'underline' }}
                  >
                    hi@discordsuperman.xyz
                  </ChakraLink>
                </VStack>
              </HStack>
            </Box>

            <Box
              p={5}
              borderRadius="md"
              bg="gray.50"
              border="1px"
              borderColor="gray.200"
              minH="100px"
              display="flex"
              alignItems="center"
            >
              <HStack spacing={4} w="full" align="center">
                <Icon
                  as={ExternalLinkIcon}
                  color="purple.500"
                  boxSize={6}
                  flexShrink={0}
                />
                <VStack align="flex-start" spacing={2} flex={1}>
                  <Text fontWeight="semibold" color="gray.800" fontSize="md">
                    {intl.formatMessage({
                      defaultMessage: 'Discord社区',
                      id: 'home.contact.discord_label',
                    })}
                  </Text>
                  <ChakraLink
                    href={discordLink}
                    color="purple.500"
                    fontWeight="medium"
                    fontSize="sm"
                    isExternal
                    _hover={{ textDecoration: 'underline' }}
                  >
                    {intl.formatMessage({
                      defaultMessage: '加入我们的服务器',
                      id: 'home.contact.discord_link_text',
                    })}
                  </ChakraLink>
                </VStack>
              </HStack>
            </Box>

            <Box
              p={5}
              borderRadius="md"
              bg="gray.50"
              border="1px"
              borderColor="gray.200"
              minH="100px"
              display="flex"
              alignItems="center"
            >
              <HStack spacing={4} w="full" align="center">
                <Icon
                  as={ExternalLinkIcon}
                  color="blue.400"
                  boxSize={6}
                  flexShrink={0}
                />
                <VStack align="flex-start" spacing={2} flex={1}>
                  <Text fontWeight="semibold" color="gray.800" fontSize="md">
                    {intl.formatMessage({
                      defaultMessage: 'Twitter',
                      id: 'home.contact.twitter_label',
                    })}
                  </Text>
                  <ChakraLink
                    href={twitterLink}
                    color="blue.400"
                    fontWeight="medium"
                    fontSize="sm"
                    isExternal
                    _hover={{ textDecoration: 'underline' }}
                  >
                    @DiscordSuperMan
                  </ChakraLink>
                </VStack>
              </HStack>
            </Box>

            <Box
              p={5}
              borderRadius="md"
              bg="gray.50"
              border="1px"
              borderColor="gray.200"
              minH="100px"
              display="flex"
              alignItems="center"
            >
              <HStack spacing={4} w="full" align="center">
                <Icon
                  as={ExternalLinkIcon}
                  color="green.500"
                  boxSize={6}
                  flexShrink={0}
                />
                <VStack align="flex-start" spacing={2} flex={1}>
                  <Text fontWeight="semibold" color="gray.800" fontSize="md">
                    {intl.formatMessage({
                      defaultMessage: 'ENS域名',
                      id: 'home.contact.ens_label',
                    })}
                  </Text>
                  <ChakraLink
                    href="https://app.ens.domains/name/discordsuperman.eth/details"
                    color="green.500"
                    fontWeight="medium"
                    fontSize="sm"
                    isExternal
                    _hover={{ textDecoration: 'underline' }}
                  >
                    discordsuperman.eth
                  </ChakraLink>
                </VStack>
              </HStack>
            </Box>
          </SimpleGrid>

          <Box textAlign="left">
            <Link href="/contact" passHref>
              <Button
                as="a"
                variant="outline"
                colorScheme="blue"
                size="lg"
                leftIcon={<EmailIcon />}
                _hover={{ textDecoration: 'none', opacity: 0.8 }}
              >
                {intl.formatMessage({
                  defaultMessage: '查看更多联系方式',
                  id: 'home.contact.view_more',
                })}
              </Button>
            </Link>
          </Box>
        </Box>
      </Stack>
    </>
  );
}

function ScreenShot(props: {
  src: string;
  alt?: string;
  caption?: string;
  width?: number;
  height?: number;
}) {
  return (
    <figure className={styles.screenshotItem}>
      <img
        src={props.src}
        alt={props.alt || ''}
        width={props.width}
        height={props.height}
        loading="lazy"
        decoding="async"
      />
      <figcaption>{props.caption}</figcaption>
    </figure>
  );
}
